import plural from 'system/libs/plural';

/**
 * Библиотека для преобразования и форматирования размеров файлов и папок.
 */
const bounds = [
    1024, // 'КБ'
    1048576, // 'МБ'
    1073741824, // 'ГБ'
    1099511627776, // 'ТБ'
    1125899906842624, // 'ПБ'
];
const units = ['КБ', 'МБ', 'ГБ', 'ТБ', 'ПБ'];
const bytesInUnit = Object.fromEntries(
    units.map((unit, index) => [unit, bounds[index]]),
);
let roundMethod = 'round';
const RE_units = new RegExp(
    '(^|\\s|\\d)((' + units.join(')|(') + '))($|\\s)',
    'ig',
);
const numberToLocaleString = Number.prototype.toLocaleString;

const NO_BREAK_SPACE = '\u00A0';
const BYTE_CASES = ['байт', 'байта', 'байт'];

function getDigitsCount(value) {
    return String(value).replace('.', '').length;
}

function round(value, precision) {
    return Math[roundMethod](value * precision) / precision;
}

function reducePrecision(bytes, divisor, maxDigitsCount, precision) {
    const roundedValue = round(bytes / divisor, precision);

    if (getDigitsCount(roundedValue) > maxDigitsCount) {
        precision = precision / 10;

        if (precision >= 1) {
            return reducePrecision(bytes, divisor, maxDigitsCount, precision);
        }
    }

    return roundedValue;
}

function getDivisor(bytes) {
    let i = bounds.length;
    let bound;

    while (i--) {
        bound = bounds[i];

        if (bound < bytes) {
            return bound;
        }
    }

    return 1;
}

function getPrecision(maxDigitsCount) {
    return 10 ** (maxDigitsCount - 1);
}

/**
 * Приводит количество байт к размеру не больше чем в N цифрах.
 * @param {number} bytes – целое число байт.
 * @param {number} maxDigitsCount – максимальное количество цифр.
 * @returns {Object}
 */
function toNDigits(bytes, maxDigitsCount, divisor) {
    let currUnits = 'байт';
    let roundedSpace = bytes;

    if (getDigitsCount(bytes) > maxDigitsCount) {
        if (!divisor) {
            divisor = getDivisor(bytes);
        }

        roundedSpace = reducePrecision(
            bytes,
            divisor,
            maxDigitsCount,
            getPrecision(maxDigitsCount),
        );

        if (getDigitsCount(roundedSpace) > maxDigitsCount) {
            return toNDigits(bytes, maxDigitsCount, divisor * 1024);
        }

        currUnits = units[bounds.indexOf(divisor)];
    }

    return {
        original: bytes,
        units: currUnits,
        space: roundedSpace,
        value: roundedSpace + ' ' + currUnits,
    };
}

const BytesUtils = {
    disableRounding() {
        roundMethod = 'floor';
    },

    enableRounding() {
        roundMethod = 'round';
    },

    toNDigits,

    megabytesToNDigits(megabytes, maxDigitsCount) {
        const space = this.toNDigits(megabytes * 1024 * 1024, maxDigitsCount);

        space.original = megabytes;

        return space;
    },

    bytesToNDigits(bytes, maxDigitsCount) {
        const space = this.toNDigits(bytes, maxDigitsCount);

        space.original = bytes;

        return space;
    },

    toLocaleString(bytes) {
        let result = bytes;

        if (numberToLocaleString) {
            result = numberToLocaleString.call(bytes);
            result = result.replace(/\s/g, NO_BREAK_SPACE);
        }

        return result + NO_BREAK_SPACE + BYTE_CASES[0];
    },

    normaliseUnitTitle(title) {
        return String(title).replace(RE_units, (str) => {
            return str.toUpperCase();
        });
    },

    toHumanFriendlyValue(bytes, fractionDigits = 2) {
        let value = Number(bytes);
        let i = units.length;

        while (i--) {
            const bound = bounds[i];

            if (value > bound) {
                value = value / bound;

                break;
            }
        }

        // Приводим к указанной точности и удаляем ненужные нули.
        value = parseFloat(value.toFixed(fractionDigits));

        let unit = units[i];

        if (!unit) {
            unit = plural(value, BYTE_CASES);
        }

        return { value, unit };
    },

    toHumanFriendlyString(bytes, fractionDigits) {
        const { value, unit } = BytesUtils.toHumanFriendlyValue(
            bytes,
            fractionDigits,
        );

        return value + NO_BREAK_SPACE + unit;
    },

    // all must be ok with translated units
    bytesToUnit(bytes, targetUnit, fractionDigits) {
        const bound = bytesInUnit[targetUnit];
        if (!bound) {
            console.error(
                `Unknown unit "${targetUnit}" provided for bytes convertation!`,
            );
            return bytes;
        }
        const result = bytes / bound;

        if (fractionDigits == null) {
            return result;
        }

        return parseFloat(result.toFixed(fractionDigits));
    },
};

export default BytesUtils;
